/**
 * 加購選擇器：每項 0/1 勾選（D11），顯示換算後顯示幣別價格（formOptions 提供）。
 */
import { Check } from "lucide-react";
import { useLanguage } from "../../../contexts/LanguageContext";
import type { AddonCode } from "../../../../../shared/booking";
import { fmtMoney, type AddonOption } from "./wizard-shared";

export function AddonPicker({
  addons,
  selected,
  onChange,
  displayCurrency,
}: {
  addons: AddonOption[];
  selected: AddonCode[];
  onChange: (codes: AddonCode[]) => void;
  displayCurrency: string;
}) {
  const { t } = useLanguage();

  if (addons.length === 0) {
    return (
      <p className="text-[13px] text-muted-foreground">
        {t({ "zh-TW": "目前沒有可加購的服務。", "en-US": "No add-ons available.", "zh-CN": "目前没有可加购的服务。" })}
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {addons.map((a) => {
        const code = a.code as AddonCode;
        const active = selected.includes(code);
        return (
          <button
            key={a.code}
            type="button"
            role="checkbox"
            aria-checked={active}
            onClick={() => onChange(active ? selected.filter((c) => c !== code) : [...selected, code])}
            className={`flex items-center justify-between gap-3 rounded-md border px-3.5 py-3 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              active ? "border-primary bg-primary-soft/40" : "border-border bg-card hover:border-border-strong"
            }`}
          >
            <span className="flex items-center gap-2.5">
              <span
                className={`flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded border transition-colors ${
                  active ? "border-primary bg-primary text-primary-foreground" : "border-border-strong bg-background"
                }`}
              >
                {active && <Check className="h-3 w-3" strokeWidth={3} />}
              </span>
              <span className="text-[13px] font-medium">{a.name}</span>
            </span>
            <span className="numeric shrink-0 text-[13px] font-medium text-foreground/80">
              {a.displayPrice !== null ? `+${fmtMoney(displayCurrency, a.displayPrice)}` : `+${fmtMoney("HKD", a.priceHkd)}`}
            </span>
          </button>
        );
      })}
    </div>
  );
}
